import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import ReportsSection from '../components/ReportsSection';
import VisaApplicationFilters from '../components/VisaApplicationFilters';
import VisaApplicationsPoolSection from '../components/VisaApplicationsPoolSection';
import VisaApplicationHistoryModal from '../components/VisaApplicationHistoryModal';
import VisaApplicationDetailModal from '../components/VisaApplicationDetailModal';
import ApplicantNameCell from '../components/ApplicantNameCell';
import VisaApplicationTableActions from '../components/VisaApplicationTableActions';
import {
  DEFAULT_VISA_FILTERS,
  ERROR_POOL_FILTERS,
  FINALIZE_POOL_FILTERS,
  filterVisaApplications,
  getApplicantName,
} from '../utils/visaApplicationFilters';
import {
  formatStatusLabel,
  getStatusBadgeClass,
  MANAGER_UPDATABLE_STATUSES,
} from '../utils/visaApplicationStatus';
import { getVisaHistoryUrl } from '../utils/visaApplicationHistoryApi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PATH_TO_TAB = {
  '/manager/dashboard': 'dashboard',
  '/manager/assign-me': 'assign_me',
  '/manager/visa-applications': 'visa_applications',
  '/manager/error-pool': 'error_pool',
  '/manager/finalize-pool': 'finalize_pool',
  '/manager/reports': 'reports',
};

const ManagerDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const getTabFromPath = () => PATH_TO_TAB[location.pathname] || 'dashboard';
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [visaApplications, setVisaApplications] = useState([]);
  const [visaFilters, setVisaFilters] = useState(DEFAULT_VISA_FILTERS);
  const [errorPoolFilters, setErrorPoolFilters] = useState(ERROR_POOL_FILTERS);
  const [finalizePoolFilters, setFinalizePoolFilters] = useState(FINALIZE_POOL_FILTERS);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [historyApplication, setHistoryApplication] = useState(null);
  const [availableApplications, setAvailableApplications] = useState([]);
  const [selectedAssignIds, setSelectedAssignIds] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [statusUpdateApp, setStatusUpdateApp] = useState(null);
  const [statusUpdateForm, setStatusUpdateForm] = useState({ application_status: 'error', status_description: '' });
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const filteredVisaApplications = useMemo(
    () => filterVisaApplications(visaApplications, visaFilters),
    [visaApplications, visaFilters]
  );

  useEffect(() => {
    const init = async () => {
      const tab = getTabFromPath();
      setActiveTab(tab);
      if (tab === 'dashboard') await fetchData(false);
      if (tab === 'assign_me') await fetchAvailableApplications(false);
      if (tab === 'visa_applications') await fetchVisaApplications(false);
      if (tab === 'error_pool' || tab === 'finalize_pool') await fetchVisaApplications(false);
      if (tab === 'reports') await fetchReports(false);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    const tab = getTabFromPath();
    if (tab !== activeTab) {
      setActiveTab(tab);
      handleTabData(tab);
    }
  }, [location.pathname]);

  const handleTabData = (tabId) => {
    if (tabId === 'dashboard') fetchData();
    if (tabId === 'assign_me') fetchAvailableApplications();
    if (tabId === 'visa_applications') fetchVisaApplications();
    if (tabId === 'error_pool' || tabId === 'finalize_pool') fetchVisaApplications();
    if (tabId === 'reports') fetchReports();
  };

  const fetchData = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/manager/dashboard`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchAvailableApplications = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/manager/visa-applications/available`);
      setAvailableApplications(res.data);
      setSelectedAssignIds([]);
      setError('');
    } catch (err) {
      console.error('Failed to fetch available applications:', err);
      setError(err.response?.data?.error || 'Failed to load available visa applications');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const toggleAssignSelection = (applicationId) => {
    const id = String(applicationId);
    setSelectedAssignIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAllAvailable = () => {
    if (selectedAssignIds.length === availableApplications.length) {
      setSelectedAssignIds([]);
      return;
    }
    setSelectedAssignIds(availableApplications.map((app) => String(app.id)));
  };

  const openAssignConfirm = () => {
    if (!selectedAssignIds.length) {
      setError('Select at least one visa application to assign');
      return;
    }
    setError('');
    setShowAssignConfirm(true);
  };

  const confirmAssignMe = async () => {
    setAssignLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/manager/visa-applications/assign`, {
        application_ids: selectedAssignIds.map(Number),
      });
      setSuccess(res.data.message);
      setSelectedAssignIds([]);
      setShowAssignConfirm(false);
      await fetchAvailableApplications();
      if (activeTab === 'dashboard') await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign visa applications');
      setShowAssignConfirm(false);
    } finally {
      setAssignLoading(false);
    }
  };

  const openStatusUpdate = (app) => {
    setStatusUpdateApp(app);
    setStatusUpdateForm({
      application_status: MANAGER_UPDATABLE_STATUSES.includes(app.application_status)
        ? app.application_status
        : 'error',
      status_description: app.status_description || '',
    });
    setError('');
  };

  const closeStatusUpdate = () => {
    if (statusUpdateLoading) return;
    setStatusUpdateApp(null);
    setStatusUpdateForm({ application_status: 'error', status_description: '' });
  };

  const confirmStatusUpdate = async () => {
    if (!statusUpdateApp) return;

    const description = statusUpdateForm.status_description.trim();
    if (!description) {
      setError('Description is required when updating status');
      return;
    }

    setStatusUpdateLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.patch(
        `${API_URL}/manager/visa-applications/${statusUpdateApp.id}/status`,
        {
          application_status: statusUpdateForm.application_status,
          status_description: description,
        }
      );
      setSuccess(res.data.message);
      setStatusUpdateApp(null);
      setStatusUpdateForm({ application_status: 'error', status_description: '' });
      await fetchVisaApplications(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update application status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const fetchVisaApplications = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/manager/visa-applications`);
      setVisaApplications(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch visa applications:', err);
      setError(err.response?.data?.error || 'Failed to load visa applications');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchReports = async (manageLoading = true) => {
    setReportLoading(true);
    try {
      const res = await axios.get(`${API_URL}/manager/reports`);
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setReport(null);
    } finally {
      setReportLoading(false);
      if (manageLoading) setLoading(false);
    }
  };

  const renderDashboard = () => (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Manager Dashboard</h1>
        <p className="text-gray-500 mt-1">Monitor all companies and system activity</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Total Companys</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalCompanies || 0}</p>
        </div>

        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Active Companys</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.activeCompanys || 0}</p>
        </div>
      </div>
    </>
  );

  const renderAssignMe = () => {
    const selectedApplications = availableApplications.filter((app) =>
      selectedAssignIds.includes(String(app.id))
    );

    const confirmModal = showAssignConfirm ? (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={() => !assignLoading && setShowAssignConfirm(false)}
      >
        <div
          className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Assignment</h3>
          <p className="text-sm text-gray-600 mb-4">
            Assign {selectedAssignIds.length} visa application
            {selectedAssignIds.length === 1 ? '' : 's'} to yourself? They will appear in your Visa Applications section.
          </p>
          <ul className="mb-6 max-h-40 overflow-y-auto space-y-2 text-sm text-gray-700">
            {selectedApplications.map((app) => (
              <li key={app.id} className="px-3 py-2 bg-gray-50 rounded-lg">
                {app.company_name} — {app.passenger_id} ({getApplicantName(app.customer_visa_application_data)})
              </li>
            ))}
          </ul>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => setShowAssignConfirm(false)}
              disabled={assignLoading}
              className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmAssignMe}
              disabled={assignLoading}
              className="px-4 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors disabled:opacity-50"
            >
              {assignLoading ? 'Assigning...' : 'Confirm Assign'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

    return (
    <>
      {confirmModal}

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Assign Me</h1>
        <p className="text-gray-500 mt-1">
          Select visa applications matching your from country, to country, and companies
        </p>
      </div>

      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <p className="text-sm text-gray-500">
            {availableApplications.length} application{availableApplications.length === 1 ? '' : 's'} available to assign
          </p>
          <button
            type="button"
            onClick={openAssignConfirm}
            disabled={assignLoading || selectedAssignIds.length === 0}
            className="px-5 py-2.5 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {assignLoading ? 'Assigning...' : `Assign Me (${selectedAssignIds.length})`}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 w-12">
                  <input
                    type="checkbox"
                    checked={
                      availableApplications.length > 0 &&
                      selectedAssignIds.length === availableApplications.length
                    }
                    onChange={toggleSelectAllAvailable}
                    disabled={availableApplications.length === 0}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Company</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger ID</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Applicant</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">From</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">To</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Visa Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {availableApplications.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-400">
                    No unassigned visa applications match your countries and companies.
                  </td>
                </tr>
              ) : (
                availableApplications.map((app) => (
                  <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedAssignIds.includes(String(app.id))}
                        onChange={() => toggleAssignSelection(app.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.company_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <ApplicantNameCell data={app.customer_visa_application_data} application={app} />
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.from_country_name || app.from_country}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.to_country_name || app.to_country}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                        {formatStatusLabel(app.application_status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">{new Date(app.created_at).toLocaleDateString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
    );
  };

  const renderApplicationDetailModal = () => (
    <VisaApplicationDetailModal
      application={selectedApplication}
      onClose={() => setSelectedApplication(null)}
      extraFields={
        selectedApplication?.company_name
          ? [{ label: 'Company', value: selectedApplication.company_name }]
          : []
      }
    />
  );

  const renderVisaApplications = () => {
    const canUpdateStatus = (app) => ['processing', 'error', 'finalize'].includes(app.application_status);

    const statusUpdateModal = statusUpdateApp ? (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={closeStatusUpdate}
      >
        <div
          className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Update Application Status</h3>
          <p className="text-sm text-gray-600 mb-4">
            {statusUpdateApp.company_name} — {statusUpdateApp.passenger_id}
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={statusUpdateForm.application_status}
                onChange={(e) => setStatusUpdateForm((prev) => ({ ...prev, application_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                disabled={statusUpdateLoading}
              >
                <option value="error">Error</option>
                <option value="finalize">Finalized</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                value={statusUpdateForm.status_description}
                onChange={(e) => setStatusUpdateForm((prev) => ({ ...prev, status_description: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm resize-none"
                placeholder="Enter a description for this status update"
                disabled={statusUpdateLoading}
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={closeStatusUpdate}
              disabled={statusUpdateLoading}
              className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmStatusUpdate}
              disabled={statusUpdateLoading}
              className="px-4 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors disabled:opacity-50"
            >
              {statusUpdateLoading ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

    const detailModal = renderApplicationDetailModal();

    return (
      <>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Visa Applications</h1>
          <p className="text-gray-500 mt-1">Visa applications you have assigned to yourself</p>
        </div>

        {detailModal}
        {statusUpdateModal}

        <VisaApplicationFilters
          applications={visaApplications}
          filters={visaFilters}
          onChange={setVisaFilters}
          onClear={() => setVisaFilters(DEFAULT_VISA_FILTERS)}
          showCompanyFilter
          totalCount={visaApplications.length}
          filteredCount={filteredVisaApplications.length}
        />

        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Applicant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">From</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">To</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Visa Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Submitted</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8 text-gray-400">
                      No assigned visa applications yet. Use the Assign Me section to claim applications.
                    </td>
                  </tr>
                ) : filteredVisaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8 text-gray-400">No applications match your filters.</td>
                  </tr>
                ) : (
                  filteredVisaApplications.map((app) => (
                    <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.company_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        <ApplicantNameCell data={app.customer_visa_application_data} application={app} />
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.from_country_name || app.from_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.to_country_name || app.to_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                          {formatStatusLabel(app.application_status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">{new Date(app.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <VisaApplicationTableActions
                          app={app}
                          onView={setSelectedApplication}
                          onViewHistory={setHistoryApplication}
                          onUpdateStatus={openStatusUpdate}
                          canUpdateStatus={canUpdateStatus(app)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderReports = () => (
    <ReportsSection
      title="System Reports"
      subtitle="Complete overview of companies and visa application activity"
      report={report}
      loading={reportLoading}
    />
  );

  const renderApplicationModals = () => {
    const statusUpdateModal = statusUpdateApp ? (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={closeStatusUpdate}
      >
        <div
          className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Update Application Status</h3>
          <p className="text-sm text-gray-600 mb-4">
            {statusUpdateApp.company_name} — {statusUpdateApp.passenger_id}
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={statusUpdateForm.application_status}
                onChange={(e) => setStatusUpdateForm((prev) => ({ ...prev, application_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                disabled={statusUpdateLoading}
              >
                <option value="error">Error</option>
                <option value="finalize">Finalized</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                value={statusUpdateForm.status_description}
                onChange={(e) => setStatusUpdateForm((prev) => ({ ...prev, status_description: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm resize-none"
                placeholder="Enter a description for this status update"
                disabled={statusUpdateLoading}
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={closeStatusUpdate}
              disabled={statusUpdateLoading}
              className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmStatusUpdate}
              disabled={statusUpdateLoading}
              className="px-4 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors disabled:opacity-50"
            >
              {statusUpdateLoading ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

    const detailModal = renderApplicationDetailModal();

    return (
      <>
        {detailModal}
        {statusUpdateModal}
      </>
    );
  };

  const renderErrorPool = () => (
    <>
      {renderApplicationModals()}
      <VisaApplicationsPoolSection
        title="Error Pool"
        subtitle="Visa applications with error status assigned to you"
        applications={visaApplications}
        filters={errorPoolFilters}
        onFiltersChange={setErrorPoolFilters}
        onClearFilters={() => setErrorPoolFilters(ERROR_POOL_FILTERS)}
        showCompanyFilter
        showCompanyColumn
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        onUpdateStatus={openStatusUpdate}
        emptyMessage="No error applications in your pool."
      />
    </>
  );

  const renderFinalizePool = () => (
    <>
      {renderApplicationModals()}
      <VisaApplicationsPoolSection
        title="Finalize Pool"
        subtitle="Finalized visa applications assigned to you"
        applications={visaApplications}
        filters={finalizePoolFilters}
        onFiltersChange={setFinalizePoolFilters}
        onClearFilters={() => setFinalizePoolFilters(FINALIZE_POOL_FILTERS)}
        showCompanyFilter
        showCompanyColumn
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        onUpdateStatus={openStatusUpdate}
        emptyMessage="No finalized applications in your pool."
      />
    </>
  );

  if (loading && activeTab !== 'reports' && activeTab !== 'visa_applications' && activeTab !== 'error_pool' && activeTab !== 'finalize_pool') {
    return (
      <Layout>
        <LoadingSpinner text="Loading dashboard..." />
      </Layout>
    );
  }

  return (
    <Layout>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
          {success}
        </div>
      )}

      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'assign_me' && renderAssignMe()}
      {activeTab === 'visa_applications' && renderVisaApplications()}
      {activeTab === 'error_pool' && renderErrorPool()}
      {activeTab === 'finalize_pool' && renderFinalizePool()}
      {activeTab === 'reports' && renderReports()}

      {historyApplication && (
        <VisaApplicationHistoryModal
          application={historyApplication}
          historyUrl={getVisaHistoryUrl(historyApplication.id, user?.role)}
          onClose={() => setHistoryApplication(null)}
        />
      )}
    </Layout>
  );
};

export default ManagerDashboard;
